import React from 'react';
import { MuiButton } from '@ace/ace-ui-web';
import type { ClickHandler } from '../../../../../utils/usageMetricsLogger';

// Components
import KpiGridSection from '../../../../../components/kpigridsection';
import SkeletonDataGrid from '../../../../../components/skeletons/common/SkeletonDataGrid';

// Styles
import pagesStandardStyles from '../../../../Pages.module.css';

// Redux
import { useAppSelector } from '../../../../../redux';
import {
  selectKpiGridsByCardName,
  selectGridLoading,
  selectKpiDashboardIsNull,
} from '../../../../../redux/OperationsReducer/operationsSlice';

// Languages
import lang from '../../../../../languages/en.json';

// Utils
import { isMobileDevice } from '../../../../../utils/appUtils';

// Constants
import { CARD_TITLES } from '../../../../../constants/constants';
import { useBuildDynamicUrl } from '../../../../../hooks/useDynamicUrl';
import { useClickHandler } from '../../../../../utils/usageMetricsLogger';

const PAGE_NM = 'Performance';
const TAB_NM = 'Ecommerce';
const SECTION_NM = 'Key Performance Indicators';

const KeyPerformanceIndicators: React.FC = () => {
  const kpiGrids = useAppSelector(state => selectKpiGridsByCardName(state, CARD_TITLES.ECOMMERCE));
  const isGridLoading = useAppSelector(selectGridLoading);
  const hasError = useAppSelector(selectKpiDashboardIsNull);
  const buildDynamicUrl = useBuildDynamicUrl();
  const handleAndLogClick = useClickHandler(PAGE_NM, TAB_NM);

  // Prefix all cell-level click IDs with the tab and section name so the DB record reads
  // "Ecommerce - Key Performance Indicators - Take Action - Order Details (AOS Validate Stock)" etc.
  const prefixedLogClick: ClickHandler = React.useCallback(
    (clickId, options) => handleAndLogClick(`${SECTION_NM} - ${clickId}`, options),
    [handleAndLogClick],
  );
  const [showPeerGroup, setShowPeerGroup] = React.useState<Record<string, boolean>>({});

  // Priority order: Loading -> Error -> No Data -> Show Data

  // 1. Show skeleton while loading
  if (isGridLoading) {
    return <SkeletonDataGrid />;
  }

  // 2. Show error message if API call failed (kpiDashboard is null)
  if (hasError) {
    const errorMessage = lang.pageContent.dataUnavailableError.replace(
      '{section}',
      lang.KeyPerformanceIndicators.sections.keyPerformanceIndicators,
    );
    return (
      <div className={pagesStandardStyles.errorContent}>
        <p>{errorMessage}</p>
      </div>
    );
  }

  // 3. Show "no data" message if API succeeded but returned empty data or no matching section
  if (!kpiGrids || kpiGrids.length === 0) {
    return (
      <div className={pagesStandardStyles.errorContent}>
        <p>{lang.pageContent.noData}</p>
      </div>
    );
  }

  const section = kpiGrids[0];

  // Action button for Key Performance Indicators
  const actionButtons =
    section.title === lang.KeyPerformanceIndicators.sections.keyPerformanceIndicators &&
    !isMobileDevice() ? (
      <MuiButton
        variant="outlined"
        iconType="launch"
        iconPosition="right"
        onClick={() => {
          const url = buildDynamicUrl('WFC', 'MRTS');
          if (url) {
            handleAndLogClick(
              `${SECTION_NM} - Top Selling Ecommerce SKUs by Territory`,
              { url, urlTarget: '_blank' },
            );
          }
        }}>
        {lang.KeyPerformanceIndicators.buttons.topSellingEcommerceSkusByTerritory}
      </MuiButton>
    ) : undefined;

  return (
    <KpiGridSection
      section={section}
      isPeerGroupVisible={showPeerGroup[section.title] ?? true}
      logClick={prefixedLogClick}
      onTogglePeerGroup={checked => {
        setShowPeerGroup(prev => ({
          ...prev,
          [section.title]: checked,
        }));
      }}
      actionButtons={actionButtons}
    />
  );
};

export default KeyPerformanceIndicators;
